import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Link as LinkIcon, Loader2, WifiOff } from 'lucide-react';
import type { PublicLobbyView } from '@rx-manager/shared';
import { BookingApiError, checkInApi } from '../../lib/api';
import { useI18n, type MessageKey } from '../../lib/i18n';
import { clockTime } from './shared';

/**
 * Track CIQ (CIQ-D28) — a lobby screen (`/l/<token>`): a TV in the waiting room showing who is
 * being called and who is next, by ticket number only — never a name and never a service, which
 * the server never sends (CIQ-D40). It looks again every 10 seconds, and keeps the last good
 * screen through a failed look. "Make a new link" on the desktop ends this one: it says so.
 *
 * The whole screen, read from across a room: no band, no panel, type sized to the screen.
 */

const POLL_MS = 10_000;

type Load = 'loading' | 'ready' | 'missing' | 'offline' | 'error';

export function LobbyPage() {
  const { token = '' } = useParams();
  const { t } = useI18n();
  const [view, setView] = useState<PublicLobbyView | null>(null);
  /** The server's clock less this screen's: a TV's own clock is often wrong. */
  const [skew, setSkew] = useState(0);
  const [load, setLoad] = useState<Load>('loading');

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const look = async (): Promise<void> => {
      try {
        const next = await checkInApi.lobby(token);
        if (cancelled) return;
        setView(next);
        const now = Date.parse(next.now);
        setSkew(Number.isFinite(now) ? now - Date.now() : 0);
        setLoad('ready');
      } catch (error) {
        if (cancelled) return;
        if (error instanceof BookingApiError && error.isMissing) {
          setLoad('missing');
          return;
        }
        const offline = error instanceof BookingApiError && error.status === 0;
        setLoad((current) => (current === 'ready' ? current : offline ? 'offline' : 'error'));
      }
      timer = setTimeout(() => void look(), POLL_MS);
    };
    void look();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [token]);

  if (load === 'missing') {
    return (
      <Notice icon={<LinkIcon className="h-[1em] w-[1em]" aria-hidden="true" />} message="lobby.missing" heading />
    );
  }
  if (!view) {
    return load === 'loading' ? (
      <Screen>
        <p className="flex items-center gap-4 text-[clamp(1.5rem,3vw,2.5rem)]">
          <Loader2 className="h-[1em] w-[1em] animate-spin" aria-hidden="true" />
          <span role="status">{t('lobby.loading')}</span>
        </p>
      </Screen>
    ) : (
      <Notice
        icon={<WifiOff className="h-[1em] w-[1em]" aria-hidden="true" />}
        message={load === 'offline' ? 'error.offline' : 'error.unknown'}
      />
    );
  }

  return (
    <Screen>
      <header className="flex flex-wrap items-baseline justify-between gap-x-8 gap-y-2">
        <h1 className="text-[clamp(1.75rem,4.5vw,4rem)] font-semibold leading-tight">{view.place.name}</h1>
        <Clock skew={skew} timeZone={view.place.timezone} />
      </header>

      <main className="mt-[3vw] grid flex-1 gap-[3vw] md:grid-cols-[3fr_2fr]">
        <section
          aria-labelledby="lobby-calling"
          className="flex flex-col rounded-[2rem] bg-card p-[3vw] text-heading shadow-panel"
        >
          <h2 id="lobby-calling" className="text-[clamp(1.5rem,3.5vw,3rem)] font-semibold text-primary-strong">
            {t('lobby.nowCalling')}
          </h2>
          {view.nowCalling.length > 0 ? (
            <ul className="mt-[2vw] flex flex-wrap items-baseline gap-x-[4vw] gap-y-[1vw]">
              {view.nowCalling.map((ticket) => (
                <li
                  key={ticket}
                  className="text-[clamp(4.5rem,15vw,13rem)] font-bold leading-none tracking-tight tabular-nums"
                >
                  {ticket}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-[2vw] text-[clamp(1.5rem,3vw,2.5rem)] text-muted-foreground">
              {t('lobby.nobodyCalled')}
            </p>
          )}
        </section>

        <section
          aria-labelledby="lobby-next"
          className="flex flex-col rounded-[2rem] bg-black/20 p-[3vw] ring-1 ring-white/15"
        >
          <h2 id="lobby-next" className="text-[clamp(1.5rem,3.5vw,3rem)] font-semibold">
            {t('lobby.next')}
          </h2>
          {view.next.length > 0 ? (
            <ul className="mt-[2vw] grid grid-cols-3 gap-x-[3vw] gap-y-[1.5vw]">
              {view.next.map((ticket) => (
                <li key={ticket} className="text-[clamp(2.5rem,7vw,6rem)] font-bold leading-none tabular-nums">
                  {ticket}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-[2vw] text-[clamp(1.5rem,3vw,2.5rem)] text-hero-muted">{t('lobby.nobodyWaiting')}</p>
          )}
        </section>
      </main>
    </Screen>
  );
}

/** The brown of the booking page's band, edge to edge. */
function Screen({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-full flex-col bg-gradient-to-br from-hero via-hero to-hero-deep px-[4vw] py-[3vw] text-hero-foreground">
      {children}
    </div>
  );
}

/** One thing to say, large, in the middle of the screen. */
function Notice({ icon, message, heading = false }: { icon: ReactNode; message: MessageKey; heading?: boolean }) {
  const { t } = useI18n();
  const Text = heading ? 'h1' : 'p';
  return (
    <Screen>
      <div className="flex flex-1 items-center justify-center">
        <Text className="flex max-w-4xl items-start gap-[0.6em] text-[clamp(1.75rem,4vw,3.5rem)] font-semibold leading-tight">
          <span className="mt-[0.15em] shrink-0">{icon}</span>
          {t(message)}
        </Text>
      </div>
    </Screen>
  );
}

/** The time on the place's own clock, kept by the server's. */
function Clock({ skew, timeZone }: { skew: number; timeZone: string }) {
  const { locale } = useI18n();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, []);
  const at = new Date(now + skew);
  return (
    <p className="text-[clamp(1.75rem,4.5vw,4rem)] font-semibold tabular-nums">
      <time dateTime={at.toISOString()}>{clockTime(at.toISOString(), timeZone, locale)}</time>
    </p>
  );
}
